import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  Plus, Wand2, Users, MessageCircle, ArrowRight,
  Check, Globe, Sparkles, Search, Inbox
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import TopNavBar from '@/components/TopNavBar';
import MarketplaceFooter from '@/components/MarketplaceFooter';

const STEPS = [
  {
    icon: Plus,
    title: 'Dodajesz',
    desc: 'miot / hodowlę / psa',
  },
  {
    icon: Wand2,
    title: 'Powstaje strona',
    desc: 'gotowa do wysłania klientowi',
  },
  {
    icon: Users,
    title: 'Klienci trafiają',
    desc: 'z wyszukiwarki',
  },
  {
    icon: MessageCircle,
    title: 'Masz zapytania',
    desc: 'piszą bezpośrednio do Ciebie',
  },
];

const BENEFITS = [
  {
    icon: Globe,
    title: 'Masz stronę bez robienia strony',
    desc: 'Twoja publiczna strona hodowli tworzy się automatycznie z kart, które wypełniasz w panelu. Bez kodowania, bez hostingu, bez wordpressów.',
  },
  {
    icon: Search,
    title: 'Nie szukasz klientów',
    desc: 'Twoja hodowla pojawia się w wyszukiwarce ApisAI i w Google. Klienci trafiają do Ciebie, gdy szukają szczeniąt danej rasy w Twojej okolicy.',
  },
  {
    icon: Inbox,
    title: 'Zbierasz zapytania w jednym miejscu',
    desc: 'Wszystkie leady z katalogu, formularzy i z Google trafiają do jednego panelu. Odpowiadasz szybko — konwertujesz więcej.',
  },
];

function CountUp({ value, className, testid }) {
  const count = useMotionValue(value);
  const rounded = useTransform(count, (v) => Math.round(v).toLocaleString('pl-PL'));
  useEffect(() => {
    const controls = animate(count, value, { duration: 0.6, ease: 'easeOut' });
    return controls.stop;
  }, [value, count]);
  return <motion.span className={className} data-testid={testid}>{rounded}</motion.span>;
}

function LeadsCalculator() {
  const [litters, setLitters] = useState(2);
  const [puppies, setPuppies] = useState(6);
  const [leadsPerPuppy, setLeadsPerPuppy] = useState(8);

  const { yearly, monthly } = useMemo(() => {
    const total = litters * puppies * leadsPerPuppy;
    return { yearly: total, monthly: Math.round(total / 12) };
  }, [litters, puppies, leadsPerPuppy]);

  const sliders = [
    { label: 'Ile miotów rocznie planujesz?', value: litters, set: setLitters, min: 1, max: 12, testid: 'slider-litters' },
    { label: 'Średnia liczba szczeniąt w miocie', value: puppies, set: setPuppies, min: 2, max: 12, testid: 'slider-puppies' },
    { label: 'Ile zapytań na 1 szczenię chcesz średnio zbierać?', value: leadsPerPuppy, set: setLeadsPerPuppy, min: 1, max: 20, testid: 'slider-leads' },
  ];

  return (
    <div className="grid md:grid-cols-[1.15fr_1fr] gap-4 md:gap-5">
      {/* LEFT: Sliders */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8" data-testid="calc-inputs">
        <div className="space-y-7">
          {sliders.map((s) => {
            const pct = ((s.value - s.min) / (s.max - s.min)) * 100;
            return (
              <div key={s.label}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-300 font-medium">{s.label}</label>
                  <span className="text-xl font-bold text-white tabular-nums" data-testid={`${s.testid}-value`}>{s.value}</span>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  value={s.value}
                  onChange={(e) => s.set(Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-white/10 range-thumb-white"
                  style={{ background: `linear-gradient(to right, #3b82f6 ${pct}%, rgba(255,255,255,0.1) ${pct}%)` }}
                  data-testid={s.testid}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: Result */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 flex flex-col" data-testid="calc-result">
        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">Szacunkowy efekt</div>
        <CountUp value={yearly} className="text-6xl md:text-7xl font-black text-white leading-none tabular-nums" testid="calc-result-yearly" />
        <div className="text-sm text-gray-400 mt-2 mb-5">potencjalnych zapytań rocznie</div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <div className="text-[11px] text-gray-500 mb-1">Średnio miesięcznie</div>
            <CountUp value={monthly} className="text-2xl font-bold text-white tabular-nums" testid="calc-result-monthly" />
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <div className="text-[11px] text-gray-500 mb-1">Rocznie</div>
            <CountUp value={yearly} className="text-2xl font-bold text-white tabular-nums" />
          </div>
        </div>

        <p className="text-xs text-gray-500 leading-relaxed mb-5">
          To nie jest gwarancja — to prosty podgląd skali. Im lepiej uzupełniona hodowla, mioty i zdjęcia, tym większa szansa na więcej zapytań.
        </p>

        <Link to="/rejestracja" className="mt-auto" data-testid="calc-cta">
          <Button className="w-full h-12 rounded-full bg-white text-black hover:bg-white/90 font-semibold text-sm">
            Dodaj hodowlę i zacznij zbierać zapytania
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function LandingHodowcy() {
  return (
    <>
      <Helmet>
        <title>Marketplace dla hodowców psów rasowych — ApisAI</title>
        <meta name="description" content="Dodaj szczenięta i odbieraj zapytania od klientów. Dodajesz ogłoszenie → kupujący znajdują je i piszą do Ciebie." />
        <link rel="canonical" href="https://apisai.pl/dla-hodowcy" />
        <meta property="og:title" content="Marketplace dla hodowców psów rasowych — ApisAI" />
        <meta property="og:description" content="Dodaj szczenięta i odbieraj zapytania od klientów. Marketplace ApisAI." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0a0f1e] via-[#0e1628] to-[#0a0f1e] text-white">
        <TopNavBar />

        {/* ================== HERO ================== */}
        <section className="relative pt-16 md:pt-24 pb-20 md:pb-28 px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto text-center relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs mb-6" data-testid="hero-eyebrow">
              🐶 Marketplace dla hodowców psów rasowych
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-6" data-testid="hero-title">
              Dodaj ogłoszenie i&nbsp;zacznij<br className="hidden md:block" /> dostawać zapytania
            </h1>
            <p className="text-gray-400 text-lg md:text-xl mb-10 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Dodajesz szczenięta lub hodowlę — klienci znajdują je i&nbsp;piszą bezpośrednio do&nbsp;Ciebie
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/dodaj-hodowle" data-testid="hero-cta-primary">
                <Button className="h-12 px-7 rounded-full bg-orange-500 text-black hover:bg-orange-400 font-semibold text-base shadow-lg shadow-orange-500/20">
                  ➕ Dodaj ogłoszenie
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ================== 4 STEPS ================== */}
        <section className="py-12 md:py-16 px-6 border-t border-white/5" data-testid="section-steps">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className="text-center" data-testid={`step-${i + 1}`}>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10 mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-1">Krok {i + 1}</div>
                    <h3 className="text-lg md:text-xl font-semibold text-white mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-400">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================== 3 BENEFITS ================== */}
        <section className="py-16 md:py-20 px-6" data-testid="section-benefits">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10 md:mb-14 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                3 rzeczy, które dostajesz<br className="hidden md:block" /> od pierwszego dnia
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                Bez ustawień. Bez integracji. Bez zbędnej roboty.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="group rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-7 hover:border-white/25 hover:bg-white/[0.05] transition-all"
                    data-testid={`benefit-${i + 1}`}
                  >
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 border border-white/10 mb-4">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-white mb-2 leading-tight">
                      {b.title}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================== LEADS CALCULATOR ================== */}
        <section className="py-16 md:py-20 px-6 border-t border-white/5" data-testid="section-roi">
          <div className="max-w-5xl mx-auto">
            <div className="mb-10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Policz swój potencjał
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                Prosty kalkulator pokazujący, ile zapytań możesz zbierać z katalogu i landingów.
              </p>
            </div>
            <LeadsCalculator />
          </div>
        </section>

        {/* ================== FINAL CTA (usunięta — duplikuje hero) ================== */}

        <MarketplaceFooter />
      </div>
    </>
  );
}
